import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { seoQaImpact } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Stat, Pill, Loading, Empty, DataTable, num } from "@/components/admin/seo/SeoUi";

export const Route = createFileRoute("/admin/seo/qa-impact")({
  head: () => ({ meta: [{ title: "Quality & impact · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: QaImpactPage,
});

function QaImpactPage() {
  const load = useServerFn(seoQaImpact);
  const q = useQuery({ queryKey: ["seo-qa-impact"], queryFn: () => load() });
  const d = q.data as any;
  const qa = d?.qa;
  const impact = d?.impact;
  const projections = (impact?.projections ?? []) as any[];

  return (
    <SeoPage
      title="Quality & impact"
      description="Every queued change re-checked against the wording and compliance rules, plus an estimate of the extra visits the biggest opportunities could bring. Estimates are ranges, never promises."
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <div className="grid gap-3 sm:grid-cols-3">
            <Stat label="Changes checked" value={num(qa?.checked ?? 0)} />
            <Stat label="Failing a rule" value={num(qa?.failing ?? 0)} />
            <Stat
              label="Estimated extra visits / month"
              value={impact?.state === "ok" ? `${num(impact.totals.low)}–${num(impact.totals.high)}` : "—"}
              sub={impact?.state === "ok" ? "Range, based on current search impressions" : "Needs Search Console data"}
            />
          </div>

          <Panel title="Quality check" subtitle="Length, formatting and advertising-compliance rules applied to every queued change.">
            {(qa?.rows ?? []).length === 0 ? (
              <Empty>No queued changes to check.</Empty>
            ) : (
              <DataTable
                head={["Page", "Change", "State", "Result", "Notes"]}
                rows={(qa.rows as any[]).map((r) => [
                  r.path,
                  r.actionType,
                  r.status,
                  <Pill key={r.id} tone={r.ok ? "good" : "bad"}>{r.ok ? "passes" : "fails"}</Pill>,
                  [...r.issues, ...r.warnings].join(" ") || "—",
                ])}
              />
            )}
          </Panel>

          <Panel title="Biggest opportunities" subtitle="Where a page already gets seen but gets clicked less than its position normally earns.">
            {projections.length === 0 ? (
              <Empty>No Search Console data collected yet, so no estimate can be made.</Empty>
            ) : (
              <DataTable
                head={["Page", "Search", "Times shown", "Clicks now", "Estimated clicks", "Extra / month"]}
                rows={projections.map((p, i) => [
                  p.path,
                  p.query ?? "—",
                  num(p.impressions),
                  num(p.currentClicks),
                  num(p.projectedClicks),
                  <span key={i}>{`${num(p.upliftLow)}–${num(p.upliftHigh)}`}</span>,
                ])}
              />
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
